import { defineComponent as _defineComponent } from 'vue'
import { Ref, ref, computed, onMounted, onBeforeMount } from 'vue';
import CDropdown from '@/components/CDropdown.vue';
import CButton from '../CButton/CButton.vue';
import CSolidColor from './CSolidColor.vue';
import Popper from 'popper.js';
import { type FullColorObject, type PaletteColorObject, type FullPaletteObject, PaletteType, useColor } from '@/composables/color';
import { debounce } from 'lodash';
import DraggableList from '@/components/DesignStudio/DraggableList.vue';

interface PaletteColorSorted extends PaletteColorObject {
    index: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CPaletteColor',
  props: {
    value: null,
    mode: { default: 'rgb' }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: FullPaletteObject): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: FullPaletteObject, mode: 'cmyk' | 'rgb' };





const paletteTypesList = [
    {
        value: PaletteType.LETTER,
        text: 'One color per letter'
    }
];

const paletteColorList = ref<PaletteColorObject[]>([]);
const paletteType = ref<string>(PaletteType.LETTER);
const dragStopIndex = ref<number | null>(null);
const selectedStopIndex = ref<number>(-1);
const containerPaletteRef: Ref<HTMLDivElement | null> = ref(null);
const solidColorRef: Ref<HTMLDivElement | null> = ref(null);
const currentColor = ref<FullColorObject | undefined>();
const instancePopper = ref<Popper | null>(null);
const openColorPicker = ref(false);
const { getColorObj } = useColor();

const paletteColorListSorted = computed<PaletteColorSorted[]>({
    get: () => (paletteColorList.value.map((color, index) => ({
            ...color,
            index
        }))),
    set: (values: PaletteColorSorted[]) => {
        paletteColorList.value = values?.map((value: PaletteColorSorted) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { index, ...rest } = value;
            return rest;
        }) || [];
        onInputChange();
    }
});

const canDeleteColor = computed(() => {
    return paletteColorList.value.length > 1;
});

const addColorStop = () => {
    paletteColorList.value.push({ hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1 });
    onInputChange();
};

const onDeleteColor = (index: number) => {
    if (index < 0 || index >= paletteColorList.value.length) return;
    if (paletteColorList.value.length <= 1) {
        console.warn('Cannot delete color, at least two colors are required.');
        return;
    }
    paletteColorList.value.splice(index, 1);
    dragStopIndex.value = null;
    selectedStopIndex.value = -1;
    onInputChange();
};

const onPaletteTypeChange = (value: PaletteType) => {
    paletteType.value = value;
    onInputChange();
};

const setActiveStop = (index: number) => {
    selectedStopIndex.value = index;
    const colorSelected = paletteColorList.value[index]
        ? paletteColorList.value[index]
        : undefined;

    // The picker emits as soon as it mounts, starting from the hex would overwrite the stored CMYK
    const source = props.mode === 'cmyk' && colorSelected?.cmyk ? { cmyk: colorSelected.cmyk } : { hex: colorSelected?.hex };
    currentColor.value = getColorObj({ ...source, a: colorSelected?.alpha || 1 });
};

const onUpdateColor = (color: FullColorObject) => {
    if (selectedStopIndex.value !== null && paletteColorList.value[selectedStopIndex.value]) {
        paletteColorList.value[selectedStopIndex.value].hex = color.hex;
        paletteColorList.value[selectedStopIndex.value].cmyk = color.cmyk;
    }
    onInputChange();
};

const openColorPickerPopper = (index: number) => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    setActiveStop(index);
    if (!containerPaletteRef.value) {
        console.warn('Container reference is not set.');
        return;
    }
    let container: HTMLDivElement = containerPaletteRef.value;
    if (containerPaletteRef.value?.parentElement) {
        container = containerPaletteRef.value.parentElement as HTMLDivElement;
    }
    instancePopper.value = new Popper(container, solidColorRef.value as HTMLDivElement, {
        placement: 'top-end',
        modifiers: {
            flip: {
                enabled: false,
                behavior: ['top', 'bottom'],
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport', // prevent overflow on window
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0 // prevent overflow on right side
                }
            },
            hide: {
                enabled: false
            },
            offset: {
                offset: '100px, -25%'
            }
        }
    });
    openColorPicker.value = true;
};

const onClosePopper = () => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    openColorPicker.value = false;
};

const debounceUpdate = debounce(() => {
    const palette: FullPaletteObject = {
        colors: paletteColorList.value,
        type: paletteType.value as PaletteType
    };
    emit('input', palette);
}, 150);

const onInputChange = () => {
    debounceUpdate();
};

onMounted(() => {
    if (props.value) {
        paletteColorList.value = props.value.colors || [];
        paletteType.value = props.value.type || PaletteType.LETTER;
    } else {
        const color1 = getColorObj({ hex: '#23E982', a: 1 });
        const color2 = getColorObj({ hex: '#000000', a: 1 });
        paletteColorList.value = [
            {
                hex: color1?.hex || '#23E982',
                cmyk: color1?.cmyk || { c: 0, m: 0, y: 0, k: 0 },
                alpha: color1?.a || 1
            } as PaletteColorObject,
            {
                hex: color2?.hex || '#000000',
                cmyk: color2?.cmyk || { c: 0, m: 0, y: 0, k: 0 },
                alpha: color2?.a || 1
            } as PaletteColorObject
        ];
        paletteType.value = PaletteType.LETTER;
    }
    onInputChange();
});

onBeforeMount(() => {
    onInputChange();
});

return { __sfc: true,props, emit, paletteTypesList, paletteColorList, paletteType, dragStopIndex, selectedStopIndex, containerPaletteRef, solidColorRef, currentColor, instancePopper, openColorPicker, getColorObj, paletteColorListSorted, canDeleteColor, addColorStop, onDeleteColor, onPaletteTypeChange, setActiveStop, onUpdateColor, openColorPickerPopper, onClosePopper, debounceUpdate, onInputChange, CDropdown, CButton, CSolidColor, DraggableList }
}

})